import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  Observable,
  switchMap,
  take,
  throwError,
} from 'rxjs';

import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AuthenticationService } from '../../services/authentication.service';
import { TokenStorageService } from '../../services/token-storage.service';
import { UserManagementService } from '../../services/user-management.service';
import { PASS_ERROR_HEADER, SKIP_TOKEN, TOKEN_EXPIRED_HEADER_NAME } from '../consts/api.const';
import { Router } from '@angular/router';
import { SnackbarService } from '../../services/snackbar.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private accessTokenSubject$ = new BehaviorSubject<string | null>(null);

  constructor(
    private tokenStorageService: TokenStorageService,
    private userManagementService: UserManagementService,
    private authenticationService: AuthenticationService,
    private snackbarService: SnackbarService,
    private router: Router,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const token = this.tokenStorageService.getAccessToken();
    let authRequest = request.clone();

    if (
      token &&
      !request.url.includes(this.authenticationService['refreshTokenUrl']) &&
      !request.headers.has(SKIP_TOKEN)
    ) {
      authRequest = this.addTokenHeader(authRequest, token);
    }

    if (request.headers.has(SKIP_TOKEN)) {
      authRequest = request.clone({
        headers: request.headers.delete(SKIP_TOKEN),
      });
    }

    let shouldThrowOutside = false;

    if (request.headers.has(PASS_ERROR_HEADER)) {
      shouldThrowOutside = true;
    }

    return next.handle(authRequest).pipe(
      catchError((err) => {
        if (
          err instanceof HttpErrorResponse &&
          (err.error.statusCode === 401 || err.error.statusCode === 403) &&
          err.headers.has(TOKEN_EXPIRED_HEADER_NAME)
        ) {
          return this.handleExpiredTokenError(request, next);
        }

        return shouldThrowOutside ? throwError(() => err) : EMPTY;
      }),
    );
  }

  private addTokenHeader(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`),
    });
  }

  private handleExpiredTokenError(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.accessTokenSubject$.next(null);
      const accessToken = this.tokenStorageService.getAccessToken();
      const refreshToken = this.tokenStorageService.getRefreshToken();

      if (!!accessToken && !!refreshToken) {
        const refreshRequest = this.authenticationService.refresh(accessToken, refreshToken);

        return refreshRequest.pipe(
          switchMap((token) => {
            this.isRefreshing = false;
            this.tokenStorageService.saveTokens(token);
            this.accessTokenSubject$.next(token.accessToken);

            return next.handle(this.addTokenHeader(request, token.accessToken));
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.tokenStorageService.removeTokens();
            this.userManagementService.removeUserInfo();

            this.snackbarService.openCustom({
              message: 'Your session has expired. Please log in again.',
              title: 'Session Expired',
              type: 'error',
              action: 'Close',
            });

            this.router.navigate(['/login']);

            return throwError(() => err);
          }),
        );
      }
    }

    return this.accessTokenSubject$.pipe(
      filter<string | null>((token) => !!token),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token as string))),
    );
  }
}
